import React, { useMemo, useRef, useState } from 'react';
import { dayLabel, toDay } from './dates';

// Courbe discrète d'une itération (aperçu du projet) : le % réalisé (paliers, à chaque mise à jour), le % du budget
// temps consommé (heures cumulées) et, en pointillé, le temps écoulé. Tant que le réalisé passe au-dessus des heures,
// on est dans le budget. Données : steering.current[].curve (GET /projects/{id}/steering).

const DONE = '#6366f1'; // indigo-500
const HOURS = '#d97706'; // amber-600
const W = 300;
const H = 56;

/** Valeur d'une série en escalier au jour `day` (dernier point connu), null avant le premier. */
const stepAt = (points, day) => {
  let value = null;
  points.forEach((p) => { if (p.day <= day) value = p.value; });
  return value;
};

export const curveSeries = (curve) => {
  const start = toDay(curve.start);
  const end = toDay(curve.end);
  const today = Math.min(toDay(curve.today), end);
  const span = Math.max(1, end - start);
  // Plusieurs mises à jour le même jour : la dernière compte (les valeurs arrivent dans l'ordre).
  const byDay = new Map();
  (curve.progress || []).forEach((p) => { const day = toDay(p.date); if (day != null) byDay.set(day, Number(p.percent)); });
  const done = [...byDay].map(([day, value]) => ({ day, value })).sort((a, b) => a.day - b.day);
  const used = (curve.hours || []).filter((p) => p.pct != null).map((p) => ({ day: toDay(p.date), value: Number(p.pct) }));
  return { start, end, today, span, done, used };
};

const ProgressCurve = ({ curve }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const series = useMemo(() => (curve ? curveSeries(curve) : null), [curve]);
  if (!series || (!series.done.length && !series.used.length)) return null;
  const { start, end, today, span, done, used } = series;
  const top = Math.max(100, ...done.map((p) => p.value), ...used.map((p) => p.value));
  const x = (day) => ((Math.min(Math.max(day, start), end) - start) / span) * W;
  const y = (value) => H - 2 - (Math.min(value, top) / top) * (H - 4);

  // Réalisé en paliers jusqu'à aujourd'hui ; heures en ligne, du début de l'itération à la dernière saisie.
  const donePath = done.length
    ? [`M${x(done[0].day)},${y(done[0].value)}`, ...done.slice(1).map((p) => `H${x(p.day)} V${y(p.value)}`), `H${x(today)}`].join(' ')
    : '';
  const usedPath = used.length ? `M${x(start)},${y(0)} ${used.map((p) => `L${x(p.day)},${y(p.value)}`).join(' ')}` : '';

  const move = (event) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    const day = Math.round(start + ((event.clientX - box.left) / box.width) * span);
    if (day > today) { setHover(null); return; }
    setHover({
      day, left: ((day - start) / span) * 100,
      done: stepAt(done, day), used: stepAt(used, day), elapsed: Math.round(((day - start) / span) * 100),
    });
  };

  return (
    <div className="mt-2">
      <div className="relative" onMouseMove={move} onMouseLeave={() => setHover(null)}>
        <svg ref={ref} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-14 w-full overflow-visible"
          role="img" aria-label="Avancement de l’itération : réalisé, heures consommées et temps écoulé">
          <line x1="0" y1={y(100)} x2={W} y2={y(100)} stroke="#e2e8f0" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <line x1={x(start)} y1={y(0)} x2={x(end)} y2={y(100)} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke" />
          {usedPath && <path d={usedPath} fill="none" stroke={HOURS} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
          {donePath && <path d={donePath} fill="none" stroke={DONE} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
          {hover && <line x1={x(hover.day)} y1="0" x2={x(hover.day)} y2={H} stroke="#94a3b8" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
        </svg>
        {/* Points du jour en HTML : le SVG est étiré (preserveAspectRatio none), un cercle y deviendrait ovale. */}
        {[[done, DONE], [used, HOURS]].filter(([points]) => points.length).map(([points, color]) => {
          const last = points[points.length - 1];
          return (
            <span key={color} className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white"
              style={{ left: `${(x(last.day) / W) * 100}%`, top: `${(y(last.value) / H) * 100}%`, background: color }} />
          );
        })}
        {hover && (
          <div className="pointer-events-none absolute -top-1 z-10 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 shadow-md"
            style={{ left: `${hover.left}%`, transform: `translate(${hover.left > 60 ? '-100%' : '0'}, -100%)` }}>
            <p className="font-semibold text-slate-800">{dayLabel(hover.day)}</p>
            <p>Réalisé : <span className="font-semibold tabular-nums text-slate-800">{hover.done != null ? `${Math.round(hover.done)} %` : '—'}</span></p>
            <p>Heures : <span className="font-semibold tabular-nums text-slate-800">{hover.used != null ? `${Math.round(hover.used)} % du budget` : '—'}</span></p>
            <p className="text-slate-400">Temps écoulé : {hover.elapsed} %</p>
          </div>
        )}
      </div>
      <p className="mt-1 flex flex-wrap gap-x-3 text-[10px] text-slate-400">
        <span className="flex items-center gap-1"><span className="h-0.5 w-3 rounded" style={{ background: DONE }} /> réalisé</span>
        <span className="flex items-center gap-1"><span className="h-0.5 w-3 rounded" style={{ background: HOURS }} /> heures (% du budget)</span>
        <span className="flex items-center gap-1"><span className="w-3 border-t border-dashed border-slate-300" /> temps écoulé</span>
      </p>
    </div>
  );
};

export default ProgressCurve;
